import { Alert, Button, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import type { ApiResponse, BooleanOperationResult } from "@entrilia/shared/api/types.ts";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader.tsx";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import { pluralize } from "@entrilia/shared/utilities/stringHelper.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { ChangeReportsGroupRequest, ReportGroup, ReportInfo } from "../../../../../../api/types/svTypes.ts";
import type { GroupType } from "../../groups/GroupType.ts";
import ReportGroupsConnector from "./ReportGroupsConnector.tsx";
import ReportGroupSelect from "./ReportGroupSelect.tsx";

interface Props {
  reports: Pick<ReportInfo, "clientCode" | "reportId">[];
  doMove: (request: ChangeReportsGroupRequest) => Promise<ApiResponse<BooleanOperationResult>>;
  groupType?: GroupType;
  onMoved?: () => void;
  onClose: () => void;
}

export default function MoveReportsToGroup({ reports, doMove, groupType, onMoved, onClose }: Props) {
  return (
    <PromptDialog open={true} sx={{ minHeight: "auto" }}>
      <ReportGroupsConnector clientCode={defined(reports[0]).clientCode}>
        <MoveReportsToGroupForm
          reports={reports}
          doMove={doMove}
          groupType={groupType}
          onMoved={onMoved}
          onClose={onClose}
        />
      </ReportGroupsConnector>
    </PromptDialog>
  );
}

const capitalizeWords = (text: string): string => text.replace(/\b\w/g, (letter) => letter.toUpperCase());

interface FormProps extends Props {
  groups?: ReportGroup[];
}

const MoveReportsToGroupForm = ({ reports, groups, doMove, groupType = "report", onMoved, onClose }: FormProps) => {
  const { hasAnyPermissionForClient } = useClientPermissionsContext();

  const [selectedGroupId, setSelectedGroupId] = useState("");

  const moveReports = useCallback(
    () =>
      doMove({
        clientCode: defined(reports[0]).clientCode,
        groupId: selectedGroupId,
        reportIds: reports.map((r) => r.reportId),
      }),
    [doMove, reports, selectedGroupId]
  );

  const { request, error, isLoading: moving } = useRequest(moveReports);

  const clientCode = defined(reports[0]).clientCode;
  const canManageGroups = hasAnyPermissionForClient(clientCode, ["ManageAllReports"]);
  const isSingleReport = reports.length === 1;
  const itemsNoun = pluralize(groupType, reports.length);

  const handleMoveClick = async () => {
    const [, requestError] = await request();
    if (!requestError) {
      onMoved?.();
      onClose();
    }
  };

  return (
    <>
      <DialogHeader
        text={
          isSingleReport
            ? `Change ${capitalizeWords(groupType)} Group`
            : `Change Group for ${reports.length} ${capitalizeWords(itemsNoun)}`
        }
        disabled={moving}
        onClose={onClose}
      />
      <Stack spacing={2} sx={{ flex: 1, justifyContent: "center", px: 2.5 }}>
        {error && <Alert severity="error">An error occurred while changing the group.</Alert>}
        <Typography sx={{ color: "text.secondary" }}>Choose a group for the selected {itemsNoun}.</Typography>
        <ReportGroupSelect
          clientCode={clientCode}
          value={selectedGroupId}
          groups={groups}
          disabled={moving}
          canCreateGroup={canManageGroups}
          onChange={setSelectedGroupId}
        />
      </Stack>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          disabled={selectedGroupId === "" || moving}
          loading={moving}
          onClick={handleMoveClick}
        >
          Change
        </Button>
      </DialogButtons>
    </>
  );
};
